import React, { useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { SearchIcon, PlaneIcon, CheckCircleIcon, ArrowLeftIcon, ArrowRightIcon, DownloadIcon, WalletIcon, BanknoteIcon, AlertCircleIcon, PartyPopperIcon, SparklesIcon, ShieldCheckIcon, MailIcon, PhoneIcon, KeyIcon } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { NIGERIAN_BANKS, DEMO_TICKETS } from '../constants/data';
import { useRefundFlow } from '../hooks/useRefundFlow';
import { useWallet } from '../hooks/useWallet';
import { useNotifications } from '../hooks/useNotifications';
import { formatNaira } from '../utils/formatCurrency';
import { formatDate, formatTime } from '../utils/dateUtils';
import { validateBVN, validateAccountNumber, validateTicketNumber, validateEmail, validatePhoneNumber } from '../utils/validators';
import { downloadReceipt } from '../utils/receiptGenerator';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Select';
import { StepProgressBar, ProcessingProgress } from '../components/ui/ProgressBar';
import { RefundOptionCard } from '../components/features/RefundOptionCard';
// Floating Cloud Component
function FloatingCloud({
  delay = 0,
  top = '20%',
  size = 'md'
}: {
  delay?: number;
  top?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = {
    sm: {
      width: 80,
      height: 40
    },
    md: {
      width: 120,
      height: 60
    },
    lg: {
      width: 160,
      height: 80
    }
  };
  const {
    width,
    height
  } = sizes[size];
  return <motion.div className="absolute pointer-events-none opacity-[0.07]" style={{
    top
  }} initial={{
    x: '-20%'
  }} animate={{
    x: '120vw'
  }} transition={{
    duration: 60 + delay * 10,
    repeat: Infinity,
    ease: 'linear',
    delay
  }} data-id="element-1253">
      <svg width={width} height={height} viewBox="0 0 200 100" fill="none" data-id="element-1254">
        <ellipse cx="50" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1255" />
        <ellipse cx="80" cy="50" rx="45" ry="30" fill="#0066FF" data-id="element-1256" />
        <ellipse cx="120" cy="55" rx="40" ry="28" fill="#0066FF" data-id="element-1257" />
        <ellipse cx="150" cy="60" rx="35" ry="25" fill="#0066FF" data-id="element-1258" />
      </svg>
    </motion.div>;
}
// Animated Plane for Processing
function ProcessingPlane() {
  return <motion.div className="relative w-20 h-20 mx-auto mb-6" animate={{
    y: [0, -10, 0]
  }} transition={{
    duration: 2,
    repeat: Infinity,
    ease: 'easeInOut'
  }} data-id="element-1259">
      <motion.div className="absolute inset-0 bg-primary-200 rounded-full" animate={{
      scale: [1, 1.2, 1],
      opacity: [0.5, 0.2, 0.5]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1260" />
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/30" data-id="element-1261">
        <motion.div animate={{
        rotate: 360
      }} transition={{
        duration: 3,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-1262">
          <PlaneIcon className="w-10 h-10 text-white" data-id="element-1263" />
        </motion.div>
      </div>
    </motion.div>;
}
// Success Confetti
function Confetti() {
  const colors = ['#0066FF', '#FFB300', '#00C853', '#E53935', '#9C27B0'];
  return <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1264">
      {[...Array(50)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full" style={{
      backgroundColor: colors[i % colors.length],
      left: `${Math.random() * 100}%`
    }} initial={{
      y: -20,
      opacity: 1,
      scale: 0
    }} animate={{
      y: '100vh',
      opacity: [1, 1, 0],
      scale: [0, 1, 1],
      rotate: Math.random() * 360
    }} transition={{
      duration: 2 + Math.random() * 2,
      delay: Math.random() * 0.5,
      ease: 'easeOut'
    }} data-id="element-1265" />)}
    </div>;
}
export function RefundFlow() {
  const navigate = useNavigate();
  const {
    state,
    steps,
    setTicketNumber,
    validateTicket,
    setRefundOption,
    setEmail,
    setPhone,
    setVerificationCode,
    sendVerificationCode,
    resendVerificationCode,
    setBvn,
    setBankCode,
    setAccountNumber,
    verifyKyc,
    processRefund,
    nextStep,
    prevStep,
    calculateRefund,
    reset
  } = useRefundFlow();
  const {
    addCredit
  } = useWallet();
  const {
    createNotification
  } = useNotifications();
  const [processingStatus, setProcessingStatus] = useState({
    percentage: 0,
    status: ''
  });
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [showConfetti, setShowConfetti] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const refundAmounts = calculateRefund();
  // Handle ticket validation
  const handleValidateTicket = async () => {
    const validation = validateTicketNumber(state.ticketNumber);
    if (!validation.valid) {
      setLocalErrors({
        ticketNumber: validation.error || 'Invalid ticket number'
      });
      return;
    }
    setLocalErrors({});
    await validateTicket();
  };
  // Handle sending verification code
  const handleSendCode = async () => {
    const errors: Record<string, string> = {};
    const emailValidation = validateEmail(state.email);
    if (!emailValidation.valid) {
      errors.email = emailValidation.error || 'Invalid email';
    }
    const phoneValidation = validatePhoneNumber(state.phone);
    if (!phoneValidation.valid) {
      errors.phone = phoneValidation.error || 'Invalid phone number';
    }
    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }
    setLocalErrors({});
    try {
      const code = await sendVerificationCode();
      if (code) {
        // Show the code in a toast for demo purposes
        toast.success(`Verification code sent! Use code: ${code}`, {
          duration: 10000,
          action: {
            label: 'Copy',
            onClick: () => {
              navigator.clipboard.writeText(code as string);
              setVerificationCode(code as string);
              toast.success('Code copied to clipboard!');
            }
          }
        });
      }
    } catch (error) {
      console.error('Error sending code:', error);
      toast.error('Failed to send verification code. Please try again.');
    }
  };
  const handleResendCode = async () => {
    try {
      const code = await resendVerificationCode();
      setResendTimer(60);
      if (code) {
        toast.success(`New code sent! Use code: ${code}`, {
          duration: 10000,
          action: {
            label: 'Copy',
            onClick: () => {
              navigator.clipboard.writeText(code as string);
              setVerificationCode(code as string);
              toast.success('Code copied to clipboard!');
            }
          }
        });
      }
    } catch (error) {
      console.error('Error resending code:', error);
      toast.error('Failed to resend code. Please try again.');
    }
  };
  // Handle KYC verification
  const handleVerifyKyc = async () => {
    const errors: Record<string, string> = {};
    // Validate verification code
    if (!state.verificationCode || state.verificationCode.length !== 6) {
      errors.verificationCode = 'Please enter the 6-digit verification code';
    }
    // Validate BVN
    const bvnValidation = validateBVN(state.bvn);
    if (!bvnValidation.valid) {
      errors.bvn = bvnValidation.error || 'Invalid BVN';
    }
    // Only validate bank details for bank payout option
    if (state.refundOption === 'bank_payout') {
      if (!state.bankCode) {
        errors.bankCode = 'Please select a bank';
      }
      const accountValidation = validateAccountNumber(state.accountNumber);
      if (!accountValidation.valid) {
        errors.accountNumber = accountValidation.error || 'Invalid account number';
      }
    }
    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }
    setLocalErrors({});
    await verifyKyc();
  };
  // Handle refund processing with notifications
  const handleProcessRefund = async () => {
    // Send processing notification
    await createNotification('refund_processing', 'Refund Processing Started', `Your refund of ${formatNaira(refundAmounts?.netRefund || 0)} is being processed.`);
    const request = await processRefund((percentage, status) => {
      setProcessingStatus({
        percentage,
        status
      });
    });
    // If store credit, add to wallet
    if (request && request.refundOption === 'store_credit') {
      const totalAmount = request.netRefund + (request.bonusAmount || 0);
      try {
        await addCredit(totalAmount, `Refund for ticket ${request.ticket.ticketNumber}`, {
          refundId: request.id,
          flightNumber: request.ticket.flight.flightNumber
        });
        // Send completion notification
        await createNotification('refund_completed', 'Refund Complete!', `${formatNaira(totalAmount)} has been added to your wallet (including ${formatNaira(request.bonusAmount || 0)} bonus).`);
        toast.success('Store credit added to your wallet!');
      } catch (error) {
        console.error('Failed to add credit to wallet:', error);
        // Send failure notification
        await createNotification('refund_failed', 'Refund Processing Error', 'Your refund was processed but we encountered an issue updating your wallet. Please contact support.');
        toast.error('Refund processed but failed to update wallet automatically. Please contact support.');
      }
    } else if (request && request.refundOption === 'bank_payout') {
      // Send bank payout completion notification
      await createNotification('refund_completed', 'Refund Complete!', `${formatNaira(request.netRefund)} has been sent to your bank account.`);
    }
  };
  // Auto-start processing when reaching step 4
  useEffect(() => {
    if (state.step === 4 && !state.isProcessing && !state.refundRequest) {
      handleProcessRefund();
    }
  }, [state.step]);
  // Send KYC required notification when user reaches step 3
  useEffect(() => {
    if (state.step === 3 && !state.isCodeSent) {
      createNotification('kyc_required', 'Identity Verification Required', 'Please verify your identity to proceed with your refund.');
    }
  }, [state.step, state.isCodeSent]);
  // Show confetti on success
  useEffect(() => {
    if (state.step === 5) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [state.step]);
  // Handle receipt download
  const handleDownloadReceipt = () => {
    if (state.refundRequest) {
      downloadReceipt(state.refundRequest);
    }
  };
  const pageVariants = {
    initial: {
      opacity: 0,
      x: 30
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: -30
    }
  };
  const renderStepContent = () => {
    switch (state.step) {
      case 0:
        return <motion.div key="step-0" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="max-w-md mx-auto" data-id="element-1266">
            <div className="text-center mb-8" data-id="element-1267">
              <motion.div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-primary-200 rounded-3xl flex items-center justify-center shadow-lg" initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} data-id="element-1268">
                <SearchIcon className="w-10 h-10 text-primary-600" data-id="element-1269" />
              </motion.div>
              <motion.h2 className="text-display-sm text-surface-900 mb-3" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} data-id="element-1270">
                Enter Your Ticket Details
              </motion.h2>
              <motion.p className="text-body-lg text-surface-600" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} data-id="element-1271">
                Enter your PNR (6 characters) or ticket number (13 digits) to
                check refund eligibility.
              </motion.p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-1272">
              <Card variant="elevated" padding="lg" className="shadow-xl" data-id="element-1273">
                <Input label="PNR or Ticket Number" placeholder="e.g., ABC123 or 0842401234567" value={state.ticketNumber} onChange={e => setTicketNumber(e.target.value.toUpperCase())} error={localErrors.ticketNumber || state.error || undefined} disabled={state.isValidating} data-id="element-1274" />

                {/* Demo Tickets */}
                <div className="mt-3 p-3 bg-surface-50 rounded-lg border border-surface-200" data-id="element-1275">
                  <p className="text-caption font-semibold text-surface-700 mb-2" data-id="element-1276">
                    Try these demo tickets:
                  </p>
                  <div className="space-y-1.5" data-id="element-1277">
                    {DEMO_TICKETS.map((demo, index) => <motion.button key={demo.pnr} onClick={() => setTicketNumber(demo.pnr)} className="w-full text-left px-3 py-2 text-body-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors flex items-center justify-between group" initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.4 + index * 0.05
                  }} whileHover={{
                    x: 4
                  }} data-id="element-1278">
                        <span className="font-medium" data-id="element-1279">{demo.pnr}</span>
                        <span className="text-caption text-surface-500 group-hover:text-primary-600 transition-colors" data-id="element-1280">
                          {demo.description}
                        </span>
                      </motion.button>)}
                  </div>
                </div>

                <Button fullWidth className="mt-6 shadow-lg shadow-primary-500/20" onClick={handleValidateTicket} loading={state.isValidating} disabled={!state.ticketNumber.trim()} icon={<SearchIcon className="w-4 h-4" data-id="element-1282" />} data-id="element-1281">
                  {state.isValidating ? 'Checking with airline...' : 'Check Eligibility'}
                </Button>
              </Card>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div className="flex items-center justify-center gap-6 mt-8 text-surface-500" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.5
          }} data-id="element-1283">
              <div className="flex items-center gap-2" data-id="element-1284">
                <ShieldCheckIcon className="w-4 h-4 text-success-500" data-id="element-1285" />
                <span className="text-body-sm" data-id="element-1286">NCAA Compliant</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-1287">
                <SparklesIcon className="w-4 h-4 text-accent-500" data-id="element-1288" />
                <span className="text-body-sm" data-id="element-1289">10% Bonus Available</span>
              </div>
            </motion.div>
          </motion.div>;
      case 1:
        return <motion.div key="step-1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="max-w-lg mx-auto" data-id="element-1290">
            <div className="text-center mb-8" data-id="element-1291">
              <motion.div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-success-100 to-success-200 rounded-3xl flex items-center justify-center shadow-lg" initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} data-id="element-1292">
                <CheckCircleIcon className="w-10 h-10 text-success-600" data-id="element-1293" />
              </motion.div>
              <h2 className="text-display-sm text-surface-900 mb-3" data-id="element-1294">
                Ticket Eligible for Refund
              </h2>
              <p className="text-body-lg text-surface-600" data-id="element-1295">
                Great news! Your ticket qualifies for a refund.
              </p>
            </div>

            {state.ticket && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-1296">
                <Card variant="elevated" padding="lg" className="mb-6 shadow-xl" data-id="element-1297">
                  <h3 className="text-heading-md text-surface-900 mb-4" data-id="element-1298">
                    Flight Details
                  </h3>
                  <div className="space-y-4" data-id="element-1299">
                    <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-primary-50 to-surface-50 rounded-xl border border-primary-100" data-id="element-1300">
                      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center" data-id="element-1301">
                        <PlaneIcon className="w-6 h-6 text-primary-600" data-id="element-1302" />
                      </div>
                      <div className="flex-1" data-id="element-1303">
                        <p className="text-body-md font-semibold text-surface-900" data-id="element-1304">
                          {state.ticket.flight.airline.name}{' '}
                          {state.ticket.flight.flightNumber}
                        </p>
                        <p className="text-body-sm text-surface-600" data-id="element-1305">
                          {state.ticket.flight.originCity} →{' '}
                          {state.ticket.flight.destinationCity}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-1306">
                        <p className="text-body-sm font-medium text-surface-900" data-id="element-1307">
                          {formatDate(state.ticket.flight.departureTime)}
                        </p>
                        <p className="text-body-sm text-surface-600" data-id="element-1308">
                          {formatTime(state.ticket.flight.departureTime)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1309">
                      <div className="p-3 bg-surface-50 rounded-lg" data-id="element-1310">
                        <p className="text-body-sm text-surface-500 mb-1" data-id="element-1311">
                          Passenger
                        </p>
                        <p className="text-body-md font-semibold text-surface-900" data-id="element-1312">
                          {state.ticket.passenger.firstName}{' '}
                          {state.ticket.passenger.lastName}
                        </p>
                      </div>
                      <div className="p-3 bg-surface-50 rounded-lg" data-id="element-1313">
                        <p className="text-body-sm text-surface-500 mb-1" data-id="element-1314">
                          Ticket Number
                        </p>
                        <p className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1315">
                          {state.ticket.ticketNumber}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>}

            {refundAmounts && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-1316">
                <Card variant="flat" padding="lg" className="mb-6 bg-gradient-to-br from-surface-50 to-primary-50/30 border border-surface-200" data-id="element-1317">
                  <h3 className="text-heading-md text-surface-900 mb-4" data-id="element-1318">
                    Refund Breakdown
                  </h3>
                  <div className="space-y-3" data-id="element-1319">
                    <div className="flex justify-between" data-id="element-1320">
                      <span className="text-body-md text-surface-600" data-id="element-1321">
                        Original Ticket Cost
                      </span>
                      <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1322">
                        {formatNaira(refundAmounts.originalAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1323">
                      <div className="flex flex-col" data-id="element-1324">
                        <span className="text-body-md text-surface-600" data-id="element-1325">
                          Processing Fee
                        </span>
                        <span className="text-caption text-surface-500" data-id="element-1326">
                          {refundAmounts.isInternal ? 'Internal Booking (10%)' : 'External Booking (15%)'}
                        </span>
                      </div>
                      <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1327">
                        -{formatNaira(refundAmounts.processingFee)}
                      </span>
                    </div>
                    <div className="border-t border-surface-200 pt-3 flex justify-between" data-id="element-1328">
                      <span className="text-body-md font-semibold text-surface-900" data-id="element-1329">
                        Net Refund
                      </span>
                      <span className="text-heading-lg text-primary-600 tabular-nums" data-id="element-1330">
                        {formatNaira(refundAmounts.netRefund)}
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>}

            <motion.div className="flex gap-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-1331">
              <Button variant="secondary" onClick={prevStep} icon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1333" />} data-id="element-1332">
                Back
              </Button>
              <Button fullWidth onClick={nextStep} icon={<ArrowRightIcon className="w-4 h-4" data-id="element-1335" />} iconPosition="right" className="shadow-lg shadow-primary-500/20" data-id="element-1334">
                Continue
              </Button>
            </motion.div>
          </motion.div>;
      case 2:
        return <motion.div key="step-2" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="max-w-2xl mx-auto" data-id="element-1336">
            <div className="text-center mb-8" data-id="element-1337">
              <h2 className="text-display-sm text-surface-900 mb-3" data-id="element-1338">
                Choose Your Refund Option
              </h2>
              <p className="text-body-lg text-surface-600" data-id="element-1339">
                Select how you'd like to receive your refund.
              </p>
            </div>

            {refundAmounts && <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-1340">
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.1
            }} data-id="element-1341">
                  <RefundOptionCard type="bank_payout" amount={refundAmounts.netRefund} timeline="20-30 minutes" selected={state.refundOption === 'bank_payout'} onSelect={() => setRefundOption('bank_payout')} data-id="element-1342" />
                </motion.div>
                <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.2
            }} data-id="element-1343">
                  <RefundOptionCard type="store_credit" amount={refundAmounts.netRefund} bonusAmount={refundAmounts.bonusAmount} timeline="Instant" selected={state.refundOption === 'store_credit'} onSelect={() => setRefundOption('store_credit')} recommended data-id="element-1344" />
                </motion.div>
              </div>}

            <motion.div className="flex gap-4" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-1345">
              <Button variant="secondary" onClick={prevStep} icon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1347" />} data-id="element-1346">
                Back
              </Button>
              <Button fullWidth onClick={nextStep} disabled={!state.refundOption} icon={<ArrowRightIcon className="w-4 h-4" data-id="element-1349" />} iconPosition="right" className="shadow-lg shadow-primary-500/20" data-id="element-1348">
                Continue
              </Button>
            </motion.div>
          </motion.div>;
      case 3:
        return <motion.div key="step-3" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="max-w-md mx-auto" data-id="element-1350">
            <div className="text-center mb-8" data-id="element-1351">
              <motion.div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary-100 to-primary-200 rounded-3xl flex items-center justify-center shadow-lg" initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15
            }} data-id="element-1352">
                <BanknoteIcon className="w-10 h-10 text-primary-600" data-id="element-1353" />
              </motion.div>
              <h2 className="text-display-sm text-surface-900 mb-3" data-id="element-1354">
                Verify Your Identity
              </h2>
              <p className="text-body-lg text-surface-600" data-id="element-1355">
                {!state.isCodeSent ? 'Enter the email and phone number used to book this flight.' : 'Enter the verification code sent to your email and phone.'}
              </p>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} data-id="element-1356">
              <Card variant="elevated" padding="lg" className="shadow-xl" data-id="element-1357">
                <div className="space-y-4" data-id="element-1358">
                  {!state.isCodeSent ? <>
                      {/* Step 1: Enter Email & Phone */}
                      <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg mb-4" data-id="element-1359">
                        <p className="text-body-sm text-primary-700 flex items-start gap-2" data-id="element-1360">
                          <ShieldCheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-1361" />
                          <span data-id="element-1362">
                            We'll send a verification code to confirm you are
                            the ticket owner.
                          </span>
                        </p>
                      </div>

                      <Input label="Email Address" type="email" placeholder="email@example.com" value={state.email} onChange={e => setEmail(e.target.value)} error={localErrors.email} disabled={state.isValidating} leftIcon={<MailIcon className="w-4 h-4" data-id="element-1364" />} hint="Must match the email used for booking" data-id="element-1363" />

                      <Input label="Phone Number" type="tel" placeholder="+234 800 000 0000" value={state.phone} onChange={e => setPhone(e.target.value)} error={localErrors.phone} disabled={state.isValidating} leftIcon={<PhoneIcon className="w-4 h-4" data-id="element-1366" />} hint="Must match the phone used for booking" data-id="element-1365" />

                      <Button fullWidth className="mt-6 shadow-lg shadow-primary-500/20" onClick={handleSendCode} loading={state.isValidating} icon={<KeyIcon className="w-4 h-4" data-id="element-1368" />} data-id="element-1367">
                        {state.isValidating ? 'Verifying...' : 'Send Verification Code'}
                      </Button>
                    </> : <>
                      {/* Step 2: Enter Verification Code & Bank Details */}
                      <motion.div initial={{
                    opacity: 0,
                    scale: 0.95
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} className="p-4 bg-success-50 border border-success-200 rounded-xl mb-4" data-id="element-1369">
                        <p className="text-body-sm text-success-700 flex items-center gap-2" data-id="element-1370">
                          <CheckCircleIcon className="w-5 h-5" data-id="element-1371" />
                          <span data-id="element-1372">
                            Verification code sent to{' '}
                            <strong data-id="element-1373">{state.email}</strong> and{' '}
                            <strong data-id="element-1374">{state.phone}</strong>
                          </span>
                        </p>
                      </motion.div>

                      <Input label="Verification Code" placeholder="Enter 6-digit code" value={state.verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))} error={localErrors.verificationCode || state.error} disabled={state.isValidating} maxLength={6} leftIcon={<KeyIcon className="w-4 h-4" data-id="element-1376" />} hint="Check your email and SMS" data-id="element-1375" />

                      <div className="pt-4 border-t border-surface-200" data-id="element-1377">
                        <Input label="BVN (Bank Verification Number)" placeholder="Enter 11-digit BVN" value={state.bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, '').slice(0, 11))} error={localErrors.bvn} disabled={state.isValidating} maxLength={11} leftIcon={<ShieldCheckIcon className="w-4 h-4" data-id="element-1379" />} data-id="element-1378" />
                      </div>

                      {state.refundOption === 'bank_payout' && <>
                          <Select label="Bank" placeholder="Select your bank" options={NIGERIAN_BANKS.map(bank => ({
                      value: bank.code,
                      label: bank.name
                    }))} value={state.bankCode} onChange={setBankCode} error={localErrors.bankCode} disabled={state.isValidating} data-id="element-1380" />

                          <Input label="Account Number" placeholder="Enter 10-digit account number" value={state.accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, '').slice(0, 10))} error={localErrors.accountNumber} disabled={state.isValidating} maxLength={10} data-id="element-1381" />

                          {state.accountName && <motion.div className="p-4 bg-success-50 border border-success-200 rounded-xl" initial={{
                      opacity: 0,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      scale: 1
                    }} data-id="element-1382">
                              <p className="text-body-sm text-success-700 flex items-center gap-2" data-id="element-1383">
                                <CheckCircleIcon className="w-5 h-5" data-id="element-1384" />
                                Account Name:{' '}
                                <strong data-id="element-1385">{state.accountName}</strong>
                              </p>
                            </motion.div>}
                        </>}

                      <div className="flex gap-3" data-id="element-1386">
                        <Button variant="ghost" onClick={() => {
                      setEmail('');
                      setPhone('');
                      setVerificationCode('');
                      setState(prev => ({
                        ...prev,
                        isCodeSent: false,
                        error: null
                      }));
                    }} disabled={state.isValidating} data-id="element-1387">
                          Change Email/Phone
                        </Button>
                        <Button fullWidth className="shadow-lg shadow-primary-500/20" onClick={handleVerifyKyc} loading={state.isValidating} data-id="element-1388">
                          {state.isValidating ? 'Verifying...' : 'Verify & Continue'}
                        </Button>
                      </div>
                    </>}
                </div>
              </Card>
            </motion.div>

            <Button variant="ghost" className="mt-4 mx-auto" onClick={prevStep} icon={<ArrowLeftIcon className="w-4 h-4" data-id="element-1390" />} data-id="element-1389">
              Back
            </Button>
          </motion.div>;
      case 4:
        return <motion.div key="step-4" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{
          duration: 0.3
        }} className="max-w-md mx-auto" data-id="element-1391">
            <div className="text-center mb-8" data-id="element-1392">
              <ProcessingPlane data-id="element-1393" />
              <h2 className="text-display-sm text-surface-900 mb-3" data-id="element-1394">
                Processing Your Refund
              </h2>
              <p className="text-body-lg text-surface-600" data-id="element-1395">
                Please wait while we process your refund. This usually takes 2-3
                minutes.
              </p>
            </div>

            <Card variant="elevated" padding="lg" className="shadow-xl" data-id="element-1396">
              <ProcessingProgress percentage={processingStatus.percentage} status={processingStatus.status || 'Initializing...'} estimatedTime={processingStatus.percentage < 100 ? '~2 minutes remaining' : undefined} data-id="element-1397" />

              <div className="mt-6 p-4 bg-surface-50 rounded-xl border border-surface-200" data-id="element-1398">
                <p className="text-body-sm text-surface-600 text-center flex items-center justify-center gap-2" data-id="element-1399">
                  <AlertCircleIcon className="w-4 h-4" data-id="element-1400" />
                  Please don't close this page while processing.
                </p>
              </div>
            </Card>
          </motion.div>;
      case 5:
        return <motion.div key="step-5" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="max-w-md mx-auto text-center relative" data-id="element-1401">
            {showConfetti && <Confetti data-id="element-1402" />}

            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-success-400 to-success-600 rounded-full flex items-center justify-center shadow-xl shadow-success-500/30" data-id="element-1403">
              <PartyPopperIcon className="w-12 h-12 text-white" data-id="element-1404" />
            </motion.div>

            <motion.h2 className="text-display-sm text-surface-900 mb-3" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-1405">
              Refund Complete!
            </motion.h2>
            <motion.p className="text-body-lg text-surface-600 mb-8" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-1406">
              {state.refundOption === 'store_credit' ? 'Your store credit has been added to your wallet.' : 'Your refund has been sent to your bank account.'}
            </motion.p>

            {state.refundRequest && refundAmounts && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-1407">
                <Card variant="elevated" padding="lg" className="mb-6 text-left shadow-xl" data-id="element-1408">
                  <h3 className="text-heading-md text-surface-900 mb-4" data-id="element-1409">
                    Refund Summary
                  </h3>
                  <div className="space-y-3" data-id="element-1410">
                    <div className="flex justify-between" data-id="element-1411">
                      <span className="text-body-md text-surface-600" data-id="element-1412">
                        Refund Type
                      </span>
                      <span className="text-body-md font-semibold text-surface-900" data-id="element-1413">
                        {state.refundOption === 'store_credit' ? 'Store Credit' : 'Bank Payout'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1414">
                      <span className="text-body-md text-surface-600" data-id="element-1415">
                        Amount
                      </span>
                      <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1416">
                        {formatNaira(state.refundRequest.netRefund + (state.refundRequest.bonusAmount || 0))}
                      </span>
                    </div>
                    {state.refundRequest.bonusAmount && state.refundRequest.bonusAmount > 0 && <div className="flex justify-between" data-id="element-1417">
                          <span className="text-body-md text-surface-600" data-id="element-1418">
                            Bonus Included
                          </span>
                          <span className="text-body-md font-semibold text-accent-600 tabular-nums" data-id="element-1419">
                            +{formatNaira(state.refundRequest.bonusAmount)}
                          </span>
                        </div>}
                    <div className="flex justify-between" data-id="element-1420">
                      <span className="text-body-md text-surface-600" data-id="element-1421">
                        Reference
                      </span>
                      <span className="text-body-md font-semibold text-surface-900 tabular-nums" data-id="element-1422">
                        {state.refundRequest.id}
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>}

            <motion.div className="space-y-3" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} data-id="element-1423">
              <Button fullWidth variant="secondary" icon={<DownloadIcon className="w-4 h-4" data-id="element-1425" />} onClick={handleDownloadReceipt} data-id="element-1424">
                Download Receipt
              </Button>

              {state.refundOption === 'store_credit' ? <div className="grid grid-cols-2 gap-3" data-id="element-1426">
                  <Button fullWidth variant="ghost" onClick={() => navigate(ROUTES.WALLET)} icon={<WalletIcon className="w-4 h-4" data-id="element-1428" />} data-id="element-1427">
                    View Wallet
                  </Button>
                  <Button fullWidth onClick={() => navigate(ROUTES.SEARCH)} icon={<PlaneIcon className="w-4 h-4" data-id="element-1430" />} className="shadow-lg shadow-primary-500/20" data-id="element-1429">
                    Book Flight
                  </Button>
                </div> : <Button fullWidth onClick={() => navigate(ROUTES.HOME)} className="shadow-lg shadow-primary-500/20" data-id="element-1431">
                  Back to Home
                </Button>}
            </motion.div>
          </motion.div>;
      default:
        return null;
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-surface-50 via-white to-surface-50 relative overflow-hidden" data-id="element-1432">
      {/* Floating Clouds Background */}
      <div className="fixed inset-0 pointer-events-none" data-id="element-1433">
        <FloatingCloud delay={0} top="15%" size="lg" data-id="element-1434" />
        <FloatingCloud delay={15} top="40%" size="md" data-id="element-1435" />
        <FloatingCloud delay={30} top="65%" size="sm" data-id="element-1436" />
      </div>

      <Header data-id="element-1437" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative" data-id="element-1438">
        {/* Progress Bar */}
        <motion.div className="mb-12" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1439">
          <StepProgressBar steps={steps} currentStep={state.step} data-id="element-1440" />
        </motion.div>

        {/* Step Content */}
        <AnimatePresence mode="wait" data-id="element-1441">{renderStepContent()}</AnimatePresence>
      </main>
    </div>;
}